import React, { useState, useEffect, useRef } from 'react';
import {
    Grid,
    Card,
    Typography,
    Pagination,
    Button,
    Box,
    Checkbox,
    Divider
} from '@mui/material';
import theme from '../../theme';
import { HorizontalLayout, VerticalLayout } from '../../common-layout';
import BTSearchBox from '../BTSearchBox';
import { BTSelectBox } from '../BTSelectBox';
import { TitleNormal } from '../../common-text';
import BTBox from '../BTBox';
import { boardStateStore } from '../../../store/BoardStateStore';

interface BTImageListData {
    id?: string;
    title: string;
    imageSrc: string | null;
    type?: string;
    displayDate?: string;
    isPublished?: boolean;
    createdAt?: Date;
}

interface BTImageListProps {
    data: BTImageListData[];
    itemsPerPage?: number;
    onAddClick?: () => void;
    onDeleteClick?: (selectedIndexes: number[]) => void;
    addButtonText?: string;
    deleteButtonText?: string;
    checkboxSelection?: boolean;
    handleCardClick?: (index: number) => void;
    filterOptions?: { label: string; value: string }[];
    filterTitle?: string;
    filterValue?: string;
    onFilterChange?: (value: string) => void;
    sortTitle?: string;
    sortOptions?: { label: string; value: 'title' | 'displayDate' | 'createdAt' }[];
    sortValue?: 'title' | 'displayDate' | 'createdAt';
    onSortChange?: (value: 'title' | 'displayDate' | 'createdAt') => void;
    pageStateKey?: string;
}

const BTImageList: React.FC<BTImageListProps> = ({
    data,
    itemsPerPage = 18,
    onAddClick,
    onDeleteClick,
    addButtonText = '추가',
    deleteButtonText = '삭제',
    checkboxSelection = false,
    handleCardClick,
    filterOptions,
    filterTitle,
    filterValue = 'all',
    onFilterChange,
    sortTitle,
    sortOptions,
    sortValue,
    onSortChange,
    pageStateKey,
}) => {
    // 초기 렌더 시 저장된 페이지 우선 복원
    const [currentPage, setCurrentPage] = useState(() => {
        if (!pageStateKey) return 1;
        const saved = boardStateStore.getBoardState(pageStateKey);
        return saved?.currentPage ?? 1;
    });
    const [filteredData, setFilteredData] = useState<BTImageListData[]>(data);
    const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
    const [searchKeywords, setSearchKeywords] = useState<string[]>([]);
    const didHydrateRef = useRef(false);

    // Restore from store (검색/필터/정렬 등)
    useEffect(() => {
        if (!pageStateKey) return;
        const saved = boardStateStore.getBoardState(pageStateKey);
  
        if (!saved) return;
        if (saved.searchQuery) {
            const keywords = String(saved.searchQuery).toLowerCase().split(' ').filter((w) => w.trim() !== '');
            setSearchKeywords(keywords);
        }
        if (saved.selectedFilter && onFilterChange) {
            onFilterChange(saved.selectedFilter);
        }
        if (saved.selectedSort && onSortChange) {
            onSortChange(saved.selectedSort as 'title' | 'displayDate' | 'createdAt');
        }
        didHydrateRef.current = true;
    }, [pageStateKey]);

    // Persist on change
    useEffect(() => {
        if (!pageStateKey || !didHydrateRef.current) return;
        const nextState = {
            currentPage,
            searchQuery: searchKeywords.join(' '),
            selectedFilter: filterValue,
            selectedSort: sortValue,
        } as const;
        boardStateStore.updateBoardState(pageStateKey, nextState);
    }, [pageStateKey, currentPage, searchKeywords, filterValue, sortValue]);

    useEffect(() => {
        const newFiltered = data.filter((item) => {
            const matchesKeyword = searchKeywords.every(keyword => item.title.toLowerCase().includes(keyword));
            const matchesFilter = filterValue === 'all' || item.type === filterValue;
            return matchesKeyword && matchesFilter;
        });

        const sorted = [...newFiltered].sort((a, b) => {
        switch (sortValue) {
            case 'title': {
                // 문자열 오름차순
                return (a.title ?? '').localeCompare(b.title ?? '');
            }
            case 'displayDate': {
                // 최신순(내림차순) — 필요시 규칙 바꿔도 됨
                const va = a.displayDate ? new Date(a.displayDate).getTime() : -Infinity;
                const vb = b.displayDate ? new Date(b.displayDate).getTime() : -Infinity;
                return vb - va;
            }
            case 'createdAt': {
                // 최신순(내림차순)
                const va = a.createdAt ? new Date(a.createdAt).getTime() : -Infinity;
                const vb = b.createdAt ? new Date(b.createdAt).getTime() : -Infinity;
                return vb - va;
            }
            default:
                return 0;
            }
        });

        setFilteredData(sorted);
        // 페이지 데이터가 바뀔 때 현재 페이지가 범위를 넘어가면 조정 (데이터가 존재할 때만)
        if (sorted.length > 0) {
            const newTotalPages = Math.ceil(sorted.length / itemsPerPage);
            if (currentPage > newTotalPages) {
                setCurrentPage(newTotalPages);
            }
        }

        const filteredIndexes = sorted.map(item => data.indexOf(item));
        setSelectedIndexes(prev => prev.filter(index => filteredIndexes.includes(index)));
    }, [data, searchKeywords, filterValue, sortValue]);

    useEffect(() => {
        // 삭제(또는 추가)로 데이터 길이가 변하면 선택 상태 초기화
        setSelectedIndexes([]);
    }, [data.length]);

    const handleSearch = (query: string) => {
        const keywords = query.toLowerCase().split(' ').filter(word => word.trim() !== '');
        setSearchKeywords(keywords);
    };

    const handleCheckboxChange = (index: number) => {
        setSelectedIndexes(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <>
            <Box width="100%" display="flex" justifyContent="center">
                {data.length === 0 ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="200px"
                        width="100%"
                        fontSize="18px"
                        color="#888"
                    >
                        데이터가 없습니다.
                    </Box>
                ) : (
                    <VerticalLayout width='100%'>
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                // 필요시 초협소 화면 대응:
                                // flexWrap: 'wrap',
                            }}
                            >
                            {/* 좌측: 필터 + 정렬 (주 영역) */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: '1 1 auto', minWidth: 0 }}>
                                {/* 필터 */}
                                {filterOptions && filterTitle && onFilterChange && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                        flexWrap: "nowrap",            // 줄바꿈 방지
                                        fontSize: "14px",
                                        color: "#6C757D",
                                    }}
                                >
                                    <b style={{ whiteSpace: "nowrap" }}>{filterTitle}</b>
                                    <BTSelectBox
                                        width="200px"
                                        value={filterValue || ''}
                                        options={filterOptions}
                                        onChange={(e) => onFilterChange(e.target.value)}
                                    />
                                </div>
                                )}

                                {/* 정렬 */}
                                {sortOptions && onSortChange && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                        flexWrap: "nowrap",            // 줄바꿈 방지
                                        fontSize: "14px",
                                        color: "#6C757D",
                                    }}
                                >
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        sx={{
                                            borderColor: "#6C757D",
                                            mx: "4px",
                                            height: "16px",
                                            alignSelf: "center" // 🔹 부모 flex 높이의 중앙에 위치
                                        }}
                                    />
                                    <b style={{ whiteSpace: "nowrap" }}>{sortTitle ?? '정렬'}</b>
                                    <BTSelectBox
                                        width="200px"
                                        value={sortValue || ''}
                                        options={sortOptions}
                                        onChange={(e) => onSortChange(e.target.value as 'title' | 'displayDate' | 'createdAt')}
                                    />
                                </div>
                                )}
                            </div>

                            {/* 우측: 검색 (고정폭 + 수축 금지) */}
                            <div style={{ flex: '0 0 260px', marginLeft: 16 }}>
                                <BTSearchBox onSearch={handleSearch} height={44} />
                            </div>
                        </div>
                        <BTBox height='16px' />
                        {filteredData.length === 0 ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="200px"
                                width="100%"
                                fontSize="18px"
                                color="#888"
                            >
                                데이터가 없습니다.
                            </Box>
                        ) : (
                            <Grid container spacing={2} sx={{ width: '100%', boxSizing: 'border-box' }}>
                                {paginatedData.map((item, index) => {
                                    const actualIndex = data.indexOf(item);
                                    return (
                                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.id}>
                                            <Card
                                                onClick={() => {
                                                    /// 아이템 클릭시 현재 페이지 상태 저장
                                                    if (pageStateKey) {
                                                        const clickState = {
                                                            currentPage,
                                                            searchQuery: searchKeywords.join(' '),
                                                            selectedFilter: filterValue,
                                                            selectedSort: sortValue,
                                                        } as const;
                                                        boardStateStore.updateBoardState(pageStateKey, clickState);
                                                    }
                                                    handleCardClick?.(actualIndex);
                                                }}
                                                sx={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    boxShadow: 'none',
                                                    border: '1px solid #ddd'
                                                }}
                                            >
                                                <Box
                                                    position="relative"
                                                    width="100%"
                                                    sx={{ aspectRatio: '1 / 1', backgroundColor: '#f5f5f5' }}
                                                >
                                                    {item.imageSrc ? (
                                                        <img
                                                            src={item.imageSrc}
                                                            alt={item.title}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                                            <Typography color="#999" fontSize="20px">No Image</Typography>
                                                        </Box>
                                                    )}
                                                    {checkboxSelection && (
                                                        <Box position="absolute" top={4} right={4} zIndex={1}>
                                                            <Checkbox
                                                                checked={selectedIndexes.includes(actualIndex)}
                                                                onChange={() => handleCheckboxChange(actualIndex)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                sx={{
                                                                    color: '#14B446 !important',
                                                                    p: 0.5,
                                                                    '& .MuiSvgIcon-root': { fontSize: 24 },
                                                                    borderRadius: '4px',
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box p={1} display="flex" flexDirection="column" alignItems="flex-start">
                                                    {
                                                        filterOptions && (
                                                        <Typography fontSize="13px" fontWeight={500} color="#888">
                                                            {filterOptions.find(opt => opt.value === item.type)?.label || ''}
                                                        </Typography>
                                                        )
                                                    }
                                                    <Typography
                                                        fontSize="16px"
                                                        fontWeight={600}
                                                        whiteSpace="pre-wrap"
                                                        overflow="hidden"
                                                        textOverflow="ellipsis"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            height: '48px',
                                                        }}
                                                    >
                                                        {item.title}
                                                    </Typography>
                                                    <Box width="100%" display="flex" justifyContent="space-between">
                                                        <Typography fontSize="13px" color="#666">
                                                            {item.displayDate || '-'}
                                                        </Typography>
                                                        <Typography fontSize="13px" color={item.isPublished ? theme.colors.bbTech : '#aaa'}>
                                                            {item.isPublished ? '✅ 게시중' : '▶️ 게시전'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </VerticalLayout>
                )}
            </Box>

            {data.length > 0 && (
                <Box width="100%" display="flex" justifyContent="center" mt={2} pb={2}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(e, value) => setCurrentPage(value)}
                        // color="primary"  // ← 유지하고 싶으면 둬도 됨. 없어도 아래 sx로 충분히 커버.
                        sx={{
                            // 기본 버튼(숫자/화살표) 텍스트/아이콘
                            '& .MuiPaginationItem-root': {
                            color: '#14B446',
                            },
                            '& .MuiPaginationItem-ellipsis': {
                            color: '#14B446',
                            },
                            '& .MuiPaginationItem-icon': {
                            color: '#14B446',
                            },

                            // 선택된 페이지 (특이성 ↑)
                            '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: '#14B446',
                            color: '#fff',
                            },
                            '& .MuiPaginationItem-root.Mui-selected:hover': {
                            backgroundColor: '#14B446',
                            },

                            // 호버(비선택 상태)
                            '& .MuiPaginationItem-root:hover': {
                            backgroundColor: 'rgba(20, 180, 70, 0.1)',
                            },
                        }}
                    />
                </Box>
            )}

            <Box width="100%" display="flex" justifyContent="space-between" mt={2}>
                {onDeleteClick && (
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: '#FC5A5A', color: 'white', fontSize: '18px', fontWeight: '700', boxShadow: 'none', '&:hover': { backgroundColor: theme.colors.colorWarn }, width: '120px', height: '48px' }}
                        onClick={() => onDeleteClick(selectedIndexes)}
                    >
                        {deleteButtonText}
                    </Button>
                )}
                {onAddClick && (
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: theme.colors.bbTech, color: 'white', fontSize: '18px', fontWeight: '700', boxShadow: 'none', '&:hover': { backgroundColor: theme.colors.bbTech }, width: '120px', height: '48px' }}
                        onClick={onAddClick}
                    >
                        {addButtonText}
                    </Button>
                )}
            </Box>
        </>
    );
};

export default BTImageList;
